/**
 * Group a flat product list into parents and their split children.
 *
 * The list is flat from the API, but a split is a real relationship: showing
 * a parent and its aliquots as six unrelated rows that happen to share a DIN
 * makes an operator work out the lineage by eye. Parents that were never
 * split stay exactly as they are — an expander on a row with nothing under it
 * would be noise.
 *
 * Pure and dependency-free so it can be unit-tested; see `productTree.test.ts`.
 */

export interface ProductNodeLike {
  id: string
  parentProductId?: string | null
  createdAt: string
}

export interface ProductGroup<T extends ProductNodeLike> {
  product: T
  /** Split aliquots of `product`, oldest first. Empty for an unsplit product. */
  children: T[]
}

/**
 * One entry per top-level product, each carrying its children.
 *
 * A child whose parent is not in the list (a different form created it, or
 * the parent was filtered out) is promoted to top level rather than dropped —
 * losing a product from the list is worse than showing it without its lineage.
 */
export function groupProductsByLineage<T extends ProductNodeLike>(products: T[]): ProductGroup<T>[] {
  const byId = new Map(products.map(p => [p.id, p]))
  const childrenOf = new Map<string, T[]>()

  for (const p of products) {
    const parentId = p.parentProductId
    if (!parentId || !byId.has(parentId)) continue
    const siblings = childrenOf.get(parentId) ?? []
    siblings.push(p)
    childrenOf.set(parentId, siblings)
  }

  const isChild = (p: T) => !!p.parentProductId && byId.has(p.parentProductId)

  return products
    .filter(p => !isChild(p))
    .map(product => ({
      product,
      children: (childrenOf.get(product.id) ?? []).sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt),
      ),
    }))
}
